/**
 * 分段控件。
 *
 * 难点不在样式而在滑块：它必须"滑"过去而不是闪过去。
 * 滑块用固定宽度 + xPercent 位移实现，见 motion 里的 useSlidingPill。
 * 动 width 会每帧触发布局重排，是最常见的掉帧来源。
 */

import { useSlidingPill } from '../motion';
import { Pressable } from './Pressable';

export interface SegmentOption<T extends string> {
	value: T;
	label: string;
}

export interface SegmentedControlProps<T extends string> {
	options: Array<SegmentOption<T>>;
	value: T;
	onChange: (next: T) => void;
	className?: string;
}

export function SegmentedControl<T extends string>({
	options,
	value,
	onChange,
	className = '',
}: SegmentedControlProps<T>): JSX.Element {
	const index = Math.max(
		0,
		options.findIndex((option) => option.value === value),
	);
	const pillRef = useSlidingPill<HTMLSpanElement>(index);

	return (
		<div
			role="tablist"
			className={`relative flex rounded-control bg-surface-2 p-1 ${className}`}
		>
			<span
				ref={pillRef}
				aria-hidden
				className="pointer-events-none absolute bottom-1 top-1 left-1 rounded-control bg-surface shadow-e1 will-change-transform"
				style={{ width: `calc((100% - 0.5rem) / ${options.length})` }}
			/>
			{options.map((option) => {
				const active = option.value === value;
				return (
					<Pressable
						key={option.value}
						role="tab"
						aria-selected={active}
						scale={0.96}
						onClick={() => onChange(option.value)}
						className={`relative z-10 flex-1 rounded-control py-1.5 text-[13px] ${
							active ? 'font-medium text-label' : 'text-label-2'
						}`}
					>
						{option.label}
					</Pressable>
				);
			})}
		</div>
	);
}
